import { z } from 'zod';
import { conditionBandSchema } from './asset.js';

/**
 * Customer-safe asset detail DTO.
 *
 * Deliberately excludes ops-internal fields (friction costs, model version,
 * confidence inputs, FMV low/high bands). RUF-267 will add snapshotHistory
 * and RUF-268 will add telemetryRecent as sibling keys under the same envelope.
 */
export const clientAssetDetailSchema = z.object({
  id: z.string().uuid(),
  // SIL-1269: the serial is never in this payload; hasSerial drives the masked
  // "Reveal" affordance, and clientAssetSerialRevealSchema is the click response.
  hasSerial: z.boolean(),
  assetClassId: z.string(),
  assetClassName: z.string().nullable(),
  condition: conditionBandSchema.nullable(),
  lifecycleStateId: z.string().nullable(),
  lifecycleStateName: z.string().nullable(),
  // Whether this asset's lifecycle state permits listing it for sale (RUF-308).
  lifecycleAllowsListing: z.boolean(),
  // Lot membership — a lot member can't be listed independently (double-listing guard).
  lotId: z.string().uuid().nullable(),
  lotName: z.string().nullable(),
  // SIL-481: producer (GET /api/client/assets/:assetId) has always returned
  // manufacturerId alongside manufacturerName — schema was missing it.
  manufacturerId: z.string().uuid().nullable(),
  manufacturerName: z.string().nullable(),
  modelName: z.string().nullable(),
  generation: z.string().nullable(),
  subClass: z.string().nullable(),
  shortDescription: z.string().nullable(),
  classAttributes: z.record(z.unknown()).nullable(),
  specFacts: z.record(z.union([z.string(), z.number(), z.boolean()])),
  specDisplaySchema: z.unknown(),
  // SIL-1648 (multi-unit inventory lines): 1 = one serialized unit, > 1 = a
  // stock line. `latestFmv.medUsd` stays PER UNIT; `lineValueUsd` is the row
  // total (v_asset_unit_value.line_value_usd = per-unit FMV x quantity), null
  // when unpriced. `includedLicenses` lists the licenses bundled with the line
  // (asset.included_licenses; null when none).
  quantity: z.number().int().positive(),
  lineValueUsd: z.number().nullable(),
  includedLicenses: z
    .array(z.object({ partNumber: z.string(), description: z.string(), quantity: z.number().int() }))
    .nullable(),
  latestFmv: z
    .object({
      medUsd: z.number(),
      pricedAt: z.string(),
      conditionBand: conditionBandSchema,
    })
    .nullable(),
  // RUF-448: location (asset.data_center_id) + managing team (asset.team_id) +
  // free-text notes (asset.notes). Mutated only via client_set_asset_location /
  // client_set_asset_notes / client_assign_asset_team (SECURITY DEFINER).
  dataCenterId: z.string().uuid().nullable(),
  dataCenterName: z.string().nullable(),
  teamId: z.string().uuid().nullable(),
  teamName: z.string().nullable(),
  notes: z.string().nullable(),
  // SIL-274: seller/customer-declared inventory tag (asset.asset_tag, 686).
  // Folded into the same admin-editable card as location/team/notes rather
  // than a dedicated endpoint — see client_set_asset_tag (692).
  assetTag: z.string().nullable(),
  // 929: one-time manual serial entry (individual orgs, TrueRead-probe assets).
  // `serialEditable` is computed server-side by client_asset_serial_editable()
  // (the same predicate the write enforces).
  serialEnteredAt: z.string().nullable(),
  serialEditable: z.boolean(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type ClientAssetDetail = z.infer<typeof clientAssetDetailSchema>;

/**
 * POST /api/client/assets/:assetId/split (SIL-1648) - carve `units` single-unit
 * assets, each with its own serial, off a multi-unit line. Serials are REQUIRED
 * and there is exactly one per unit; admin only.
 */
export const clientSplitAssetLineRequestSchema = z.object({
  units: z.number().int().min(1),
  // One serial per unit. The count is checked by the route (not a zod refine)
  // so a mismatch returns the same `serial_count_mismatch` code the database
  // function uses.
  serials: z.array(z.string()),
});
export type ClientSplitAssetLineRequest = { units: number; serials: string[] };

export const clientSplitAssetLineResponseSchema = z.object({
  lineId: z.string().uuid(),
  newAssetIds: z.array(z.string().uuid()),
});
export type ClientSplitAssetLineResponse = z.infer<typeof clientSplitAssetLineResponseSchema>;

/** GET /api/client/assets/:assetId/serial (SIL-1269) - audited reveal. */
export const clientAssetSerialRevealSchema = z.object({ serialNumber: z.string() });
export type ClientAssetSerialReveal = z.infer<typeof clientAssetSerialRevealSchema>;

/**
 * A single entry in the asset's FMV snapshot history.
 * Deliberately exposes only median — low/high bands are ops-internal.
 */
export const clientSnapshotHistoryItemSchema = z.object({
  snapshotId: z.string().uuid(),
  pricedAt: z.string(),
  fmvMedUsd: z.number(),
  conditionBand: conditionBandSchema,
});
export type ClientSnapshotHistoryItem = z.infer<typeof clientSnapshotHistoryItemSchema>;

/**
 * A single day of aggregated device telemetry (RUF-268).
 * Exposes only the customer-safe subset — ops-internal columns (XID errors,
 * extraAggregates, etc.) are not included.
 */
export const clientTelemetryDaySchema = z.object({
  day: z.string(),
  avgGpuTempC: z.number().nullable(),
  maxGpuTempC: z.number().nullable(),
  avgGpuUtilPct: z.number().nullable(),
  avgPowerDrawWatts: z.number().nullable(),
  derivedConditionBand: z.string().nullable(),
});
export type ClientTelemetryDay = z.infer<typeof clientTelemetryDaySchema>;

/**
 * A single event on the Asset Detail lifecycle timeline (asset_lifecycle_event).
 * Three shapes, discriminated by `eventType` (migration 678):
 *   - 'lifecycle_state' (498): a decision='advanced' stage transition; fromState/
 *     toState are asset_lifecycle_state ids, the component derives the label.
 *   - 'location_change' (678, RUF-448): asset.data_center_id changed; fromLabel/
 *     toLabel are the data_center display names captured at write time.
 *   - 'team_change' (678, RUF-380): asset.team_id changed; fromLabel/toLabel are
 *     team names. Admin-only — the API filters these out for non-admin members.
 * Both are internal/org-only — the buyer-facing passport never renders lifecycle
 * events of any kind.
 */
const clientLifecycleEventCommonFields = {
  eventId: z.string().uuid(),
  createdAt: z.string(),
};

export const clientLifecycleEventSchema = z.discriminatedUnion('eventType', [
  z.object({
    eventType: z.literal('lifecycle_state'),
    ...clientLifecycleEventCommonFields,
    fromState: z.string().nullable(),
    toState: z.string().nullable(),
  }).strict(),
  z.object({
    eventType: z.literal('location_change'),
    ...clientLifecycleEventCommonFields,
    fromLabel: z.string().nullable(),
    toLabel: z.string().nullable(),
  }).strict(),
  z.object({
    eventType: z.literal('team_change'),
    ...clientLifecycleEventCommonFields,
    fromLabel: z.string().nullable(),
    toLabel: z.string().nullable(),
  }).strict(),
]);
export type ClientLifecycleEvent = z.infer<typeof clientLifecycleEventSchema>;

/**
 * Envelope for GET /api/client/assets/:assetId.
 *
 * Structured as an object with named keys so RUF-267 (snapshotHistory) and
 * RUF-268 (telemetryRecent) can add sibling keys without breaking existing
 * consumers of `detail`.
 */
export const clientAssetDetailResponseSchema = z.object({
  detail: clientAssetDetailSchema,
  snapshotHistory: z.array(clientSnapshotHistoryItemSchema),
  telemetryRecent: z.array(clientTelemetryDaySchema),
  lifecycleEvents: z.array(clientLifecycleEventSchema),
  sellerMetadata: z
    .object({
      purchasePriceUsd: z.number().nullable(),
      purchaseDate: z.string().nullable(),
      internalLocation: z.string().nullable(),
      notes: z.string().nullable(),
      updatedAt: z.string(),
    })
    .nullable(),
});
export type ClientAssetDetailResponse = z.infer<typeof clientAssetDetailResponseSchema>;

/**
 * Body for PUT /api/client/assets/:assetId/metadata (RUF-279).
 * All fields optional — a partial upsert merges into the existing row.
 * purchasePriceUsd must be positive when provided.
 */
export const assetSellerMetadataUpsertSchema = z.object({
  purchasePriceUsd: z.number().positive().nullable().optional(),
  // YYYY-MM-DD with a sane year range — native <input type="date"> lets a
  // partial year-field entry through (e.g. 2023 typed as 0203), which then
  // corrupts FMV age math downstream. Reject implausible dates at the boundary.
  purchaseDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'purchaseDate must be YYYY-MM-DD')
    .refine((s) => {
      const y = Number(s.slice(0, 4));
      return y >= 1990 && y <= 2100;
    }, 'purchaseDate year out of range (1990-2100)')
    .nullable()
    .optional(),
  internalLocation: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type AssetSellerMetadataUpsert = z.infer<typeof assetSellerMetadataUpsertSchema>;

/**
 * Body for PUT /api/client/assets/:assetId/details (RUF-448; assetTag added SIL-274).
 * All fields optional/independent — a partial update sets only what's provided.
 * dataCenterId: null clears the location; omitted leaves it unchanged.
 * notes: null clears notes; omitted leaves it unchanged.
 * assetTag: '' normalizes to null (clears the tag, mirrors the ops
 * assetTagUpdateRequestSchema); omitted leaves it unchanged.
 */
export const assetDetailsUpdateSchema = z.object({
  dataCenterId: z.string().uuid().nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
  assetTag: z
    .string()
    .max(128)
    .nullable()
    .optional()
    .transform((v) => (v === '' ? null : v)),
});
export type AssetDetailsUpdate = z.infer<typeof assetDetailsUpdateSchema>;

/**
 * One-time manual serial entry (929) - PUT /api/client/assets/:assetId/serial.
 *
 * The same rules are re-asserted inside client_set_asset_serial() (SQL) - keep
 * the two in step: trim, 1-64 chars, [A-Za-z0-9-] only (no spaces / control /
 * non-ASCII), and none of the placeholder values below. Stored trim+upper.
 */
export const MANUAL_SERIAL_MAX_LENGTH = 64;
const MANUAL_SERIAL_PLACEHOLDERS = new Set(['UNKNOWN', 'NONE', 'NULL', 'NA', 'TBD', 'DEFAULT', 'NOSERIAL']);

/** Canonical stored form: trim + upper. */
export function normalizeManualSerial(raw: string): string {
  return raw.trim().toUpperCase();
}

/** Null when acceptable, else a customer-facing reason. */
export function manualSerialProblem(raw: string): string | null {
  const s = normalizeManualSerial(raw);
  if (s.length === 0) return 'Enter the serial number.';
  if (s.length > MANUAL_SERIAL_MAX_LENGTH) return `Serial numbers are at most ${MANUAL_SERIAL_MAX_LENGTH} characters.`;
  if (!/^[A-Z0-9-]+$/.test(s)) return 'Use letters, numbers and hyphens only.';
  if (MANUAL_SERIAL_PLACEHOLDERS.has(s) || /^0+$/.test(s) || /^-+$/.test(s)) {
    return 'That looks like a placeholder, not a real serial number.';
  }
  return null;
}

export const assetSerialEntrySchema = z.object({
  serial: z.string().superRefine((v, ctx) => {
    const problem = manualSerialProblem(v);
    if (problem) ctx.addIssue({ code: z.ZodIssueCode.custom, message: problem });
  }),
});
export type AssetSerialEntry = z.infer<typeof assetSerialEntrySchema>;

export const assetSerialEntryResultSchema = z.object({
  assetId: z.string().uuid(),
  userProvidedSerial: z.string(),
});
export type AssetSerialEntryResult = z.infer<typeof assetSerialEntryResultSchema>;

/**
 * Result of PUT /api/client/assets/:assetId/details (RUF-448; assetTag added SIL-274).
 */
export const assetDetailsUpdateResultSchema = z.object({
  assetId: z.string().uuid(),
  dataCenterId: z.string().uuid().nullable(),
  dataCenterName: z.string().nullable(),
  notes: z.string().nullable(),
  assetTag: z.string().nullable(),
  updatedAt: z.string().nullable(),
});
export type AssetDetailsUpdateResult = z.infer<typeof assetDetailsUpdateResultSchema>;

/**
 * Attachment metadata (RUF-449) — cert/warranty docs uploaded via the
 * presigned-URL 3-phase flow. Never carries a URL directly; downloads are
 * always a freshly minted presigned GET fetched on demand.
 */
export const clientAssetAttachmentSchema = z.object({
  id: z.string().uuid(),
  fileName: z.string(),
  contentType: z.string(),
  sizeBytes: z.number(),
  uploadedBy: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type ClientAssetAttachment = z.infer<typeof clientAssetAttachmentSchema>;

/** Body for POST /api/client/assets/:assetId/attachments (presign request). */
export const assetAttachmentPresignSchema = z.object({
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1),
  contentLength: z.number().int().positive(),
});
export type AssetAttachmentPresignBody = z.infer<typeof assetAttachmentPresignSchema>;

/** Body for POST /api/client/assets/:assetId/attachments/finalize. */
export const assetAttachmentFinalizeSchema = z.object({
  s3Key: z.string().min(1),
  fileName: z.string().min(1).max(255),
  contentType: z.string().min(1),
  sizeBytes: z.number().int().positive(),
});
export type AssetAttachmentFinalizeBody = z.infer<typeof assetAttachmentFinalizeSchema>;
/**
 * Result of POST /api/client/assets/:assetId/lifecycle/advance (RUF-280).
 * decision='advanced' means the state was updated; 'rejected' means it was not.
 * reason is null on success, or a machine-readable token on rejection
 * (gpu_utilization_too_high | invalid_state | not_found).
 */
export const lifecycleAdvanceResultSchema = z.object({
  newState: z.string().nullable(),
  decision: z.enum(['advanced', 'rejected']),
  reason: z.string().nullable(),
});
export type LifecycleAdvanceResult = z.infer<typeof lifecycleAdvanceResultSchema>;
